/**
 * NOT-95 — polygon-based tax-zone preview & rollup types.
 *
 * Mirrors the wire contract of `POST /tax-zones/preview-from-polygon`
 * (operations · apps/operations/src/modules/taxes/tax-zones/dto/preview-from-polygon.dto.ts).
 *
 * Step 1 (zip resolver) and step 2 (bulk rate fetch) are merged. Step 3
 * extends the response with a `rollup` field — the FE is built against the
 * full shape so step 3 lights up automatically when its data lands.
 */

/** Authority hierarchy levels, federal → most specific. */
export type TaxAuthorityLevel = 'federal' | 'state' | 'county' | 'city' | 'special-district';

/**
 * Display order for level groups in the review UI.
 * Federal sits on top, then state → county → city → special-district.
 */
export const TAX_AUTHORITY_LEVEL_ORDER: readonly TaxAuthorityLevel[] = [
  'federal',
  'state',
  'county',
  'city',
  'special-district',
] as const;

/** Human-friendly label per level. */
export const TAX_AUTHORITY_LEVEL_LABEL: Record<TaxAuthorityLevel, string> = {
  federal: 'Federal',
  state: 'State',
  county: 'County',
  city: 'City',
  'special-district': 'Special district',
};

/** Per-ZIP rate row (NOT-95 step 2 contract). */
export interface PolygonPreviewRateEntry {
  zip: string;
  rate: {
    geoState: string;
    geoCounty: string | null;
    geoCity: string | null;
    stateSalesTax: number;
    countySalesTax: number;
    citySalesTax: number;
    districtSalesTax: number;
    totalSalesTax: number;
  };
}

/** A single rolled-up authority row (NOT-95 step 3 contract). */
export interface PolygonAuthorityRollup {
  /** sha1-12 — stable React key. */
  id: string;
  level: TaxAuthorityLevel;
  name: string;
  /** Decimal rate (0.0725 = 7.25%). */
  rate: number;
  zipCount: number;
  /** 0 – 1. */
  zipCoverage: number;
  /** Up to 5. */
  sampleZips: string[];
}

/**
 * An authority that resolved to multiple distinct rates across the polygon.
 * The admin must pick one rate before the writeback is allowed.
 */
export interface PolygonAuthorityConflict {
  level: string;
  name: string;
  rates: Array<{
    rate: number;
    zipCount: number;
    sampleZips: string[];
  }>;
}

/** Top-level rollup envelope (NOT-95 step 3 contract). */
export interface PolygonAuthorityRollupSection {
  authorities: PolygonAuthorityRollup[];
  totalZips: number;
  /**
   * ZIPs the provider returned an error for (cached lastError). The FE
   * surfaces these with a red "failed" badge — manual retry/skip needed.
   */
  failedZips: string[];
  /**
   * ZIPs that missed the cache and are awaiting an enqueued Ziptax
   * refresh. The FE surfaces these with an amber "resolving" badge and
   * re-polls until they drain. Optional so older BE deploys that don't
   * yet emit this field still typecheck — the consumer falls back to
   * `[]` and shows nothing.
   */
  pendingZips?: string[];
  conflicts: PolygonAuthorityConflict[];
}

/**
 * Full response shape for `POST /tax-zones/preview-from-polygon`.
 *
 * The `rollup` field is added by NOT-95 step 3 (in-flight at the time of
 * writing). We mark it optional so step 2-only deployments still typecheck;
 * the UI degrades gracefully (rolls up nothing, shows the per-ZIP list only).
 */
export interface PolygonPreviewResponse {
  zips: string[];
  rates: PolygonPreviewRateEntry[];
  failedZips: string[];
  /**
   * NOT-XX — split out from failedZips so the UI can show "resolving"
   * vs "failed" badges. Optional for backwards-compat: older BE deploys
   * won't send the field.
   */
  pendingZips?: string[];
  rollup?: PolygonAuthorityRollupSection;
}

/** Wire-level polygon body. GeoJSON convention: coordinates are [lng, lat]. */
export interface PolygonPreviewRequest {
  polygon: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
}

// ─── NOT-99 — split polygon preview (Phase A + Phase B) ─────────────────────
//
// Mirrors `apps/operations/src/modules/taxes/tax-zones/dto/preview-rates.dto.ts`.
// Phase A returns just the ZIPs (sub-second) so the FE can show the review
// view immediately. Phase B enqueues the slow Ziptax fan-out + authority
// rollup as a BullMQ job; the FE polls until it hits a terminal state.

/** Response for `POST /tax-zones/preview-from-polygon-zips`. */
export interface PolygonPreviewZipsResponse {
  zips: string[];
  /**
   * De-duplicated 2-letter state codes covering every ZIP in `zips`.
   * Added alongside `zips` so the FE map can eagerly fetch per-state
   * ZCTA GeoJSON when bulk-selecting from a polygon. Optional in the
   * type so older operations deploys that haven't shipped this field
   * still typecheck; the `ServiceAreaMap` consumer defends with `?? []`.
   */
  states?: string[];
}

/** Request body for `POST /tax-zones/preview-rates`. */
export interface PolygonPreviewRatesRequest {
  zips: string[];
}

/** Enqueue ack from `POST /tax-zones/preview-rates`. */
export interface PolygonPreviewRatesEnqueueResponse {
  jobId: string;
  status: 'queued';
  statusEndpoint: string;
}

/** Per-ZIP + rollup result the worker produces (also lives in the status response). */
export interface PolygonPreviewRatesResult {
  rates: PolygonPreviewRateEntry[];
  failedZips: string[];
  /**
   * ZIPs that missed the cache; the BE has enqueued a background refresh.
   * The FE should re-call `preview-rates` with the same ZIPs after a few
   * seconds. Optional for backwards-compat with pre-split BE deploys.
   */
  pendingZips?: string[];
  rollup: PolygonAuthorityRollupSection;
}

/** Response from `GET /tax-zones/preview-rates/:jobId` — the FE polls this. */
export interface PolygonPreviewRatesStatusResponse {
  jobId: string;
  status: 'pending' | 'done' | 'failed';
  result?: PolygonPreviewRatesResult;
  error?: string;
}

/**
 * Local resolution of a polygon-rollup conflict. Maps an authority
 * `{level, name}` key (joined by `::`) to the rate the admin chose.
 *
 * Why the joined key: conflicts are scoped to a (level, name) pair —
 * "City of Austin" can exist at the city level *and* the special-district
 * level with different rates and that's a legitimate distinction.
 */
export type PolygonConflictResolution = Record<string, number>;

/** Build the canonical key used by `PolygonConflictResolution`. */
export function conflictKey(conflict: Pick<PolygonAuthorityConflict, 'level' | 'name'>): string {
  return `${conflict.level}::${conflict.name}`;
}

// ─── NOT-95 step 5 — create-from-polygon writeback contract ─────────────────
//
// Mirrors `apps/operations/src/modules/taxes/tax-zones/dto/create-from-polygon.dto.ts`.
// Trust model: the admin only picks WHICH authorities to persist + which rate
// to use when a (level, name) bucket has multiple rates in the preview. The
// server re-resolves the polygon and re-fetches rates — the wire rate values
// are NOT trusted for write. Anything that no longer matches a fresh authority
// is surfaced under `dropped` so the UI can call out drift.

/** GeoJSON Polygon — `[outerRing, ...holes]` with `[lng, lat]` points. */
export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

/** GeoJSON MultiPolygon — array of Polygon coordinate arrays. */
export interface GeoJSONMultiPolygon {
  type: 'MultiPolygon';
  coordinates: number[][][][];
}

/** Admin-resolved conflict pick — same (level, name) we surfaced in step 3. */
export interface PolygonConflictResolutionEntry {
  level: string;
  name: string;
  /** Decimal rate (0..1) the admin picked from the preview. */
  chosenRate: number;
}

/** Request body for `POST /tax-zones/create-from-polygon`. */
export interface CreateTaxZoneFromPolygonRequest {
  polygon: GeoJSONPolygon | GeoJSONMultiPolygon;
  /** sha1-12 authority ids the admin picked in the review UI. */
  selectedAuthorityIds: string[];
  /** Empty/omitted when no conflicts surfaced in the preview. */
  conflictResolutions?: PolygonConflictResolutionEntry[];
  /** Human-readable label (1..80 chars). Idempotence key with polygon hash. */
  zoneLabel: string;
}

/**
 * Why a step-5 write dropped an admin-selected authority id.
 * - `stale-rate`: the (level, name) bucket no longer exists in the fresh rollup.
 * - `rate-changed`: the bucket still exists but its rate moved, so the
 *   deterministic id no longer matches — we don't silently adopt a number
 *   the admin didn't approve.
 */
export type DroppedAuthorityReason = 'stale-rate' | 'rate-changed';

/** Per-id reason record from the step-5 response. */
export interface DroppedAuthorityEntry {
  id: string;
  reason: DroppedAuthorityReason;
}

/** Response shape for `POST /tax-zones/create-from-polygon`. */
export interface CreateTaxZoneFromPolygonResponse {
  /** ID of the TaxGroup/zone the admin's selections wrote to. */
  taxZoneId: string;
  /** Tax codes newly minted by this call. */
  created: { taxCodeIds: string[] };
  /** Tax codes that already existed and were reused. */
  reused: { taxCodeIds: string[] };
  /** Admin-selected ids that no longer match anything fresh. */
  dropped: DroppedAuthorityEntry[];
  /**
   * ZIPs the provider errored for in the fresh fetch — skipped on this
   * write but surfaced so the admin sees them.
   */
  failedZips: string[];
  /**
   * ZIPs that missed the cache in the fresh fetch — also skipped on this
   * write, but they'll resolve on a later preview/poll once the background
   * refresh fills the cache. Optional for pre-split BE deploys.
   */
  pendingZips?: string[];
}
